import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import CampaignIcon from '@mui/icons-material/Campaign';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { Alert, Box, Chip, IconButton, Link, Stack, Tooltip } from '@mui/material';
import { DataGrid, GridColDef, GridSortModel, GridToolbar } from '@mui/x-data-grid';

import { useGetAccommodationProperties } from '~/hooks/apis/accommodation/useGetAccommodationProperties';

import { AttachedBundle, AttachedCampaign, PropertyResponse, deleteProperty } from '~/services/AccommodationService';

import { AccommodationPropertiesSearchInput } from './AccommodationPropertiesSearchForm';
import AccommodationV2BookableChip from './AccommodationV2BookableChip';
import { isBundleCurrentlyLive, isCampaignCurrentlyLive } from './attachedPromotions';
import {
  type IndicativePropertyStateFlag,
  getIndicativePropertyStateQueryFilters,
} from './indicativePropertyStateFilters';

interface Props {
  searchInput: AccommodationPropertiesSearchInput;
  pageIndex?: number;
  onPageChange: (pageIndex: number) => void;
}

const PAGE_SIZE = 20;
const ROW_HEIGHT = 68;

/** Columns hidden by default - users can enable via column selector */
const DEFAULT_HIDDEN_COLUMNS = {
  category: false,
  rating: false,
  city: false,
  nameScore: false,
  distance: false,
} as const;

const getRateChipState = (label: string, isAttached: boolean, ratesEnabled: boolean, hasAnyRatesEnabled: boolean) => {
  if (!isAttached) {
    return {
      color: 'default' as const,
      tooltip: `${label} is not attached to this property.`,
    };
  }

  if (ratesEnabled) {
    return {
      color: 'success' as const,
      tooltip: `${label} is attached and rates are enabled.`,
    };
  }

  if (hasAnyRatesEnabled) {
    return {
      color: 'warning' as const,
      tooltip: `${label} is attached but rates are disabled; another attached path has rates enabled.`,
    };
  }

  return {
    color: 'error' as const,
    tooltip: `${label} is attached but rates are disabled, and no attached path currently has rates enabled.`,
  };
};

const renderRateChip = (label: string, isAttached: boolean, ratesEnabled: boolean, hasAnyRatesEnabled: boolean) => {
  const { color, tooltip } = getRateChipState(label, isAttached, ratesEnabled, hasAnyRatesEnabled);
  const unattachedStyles = !isAttached
    ? {
        backgroundColor: 'grey.50',
        borderColor: 'grey.300',
        color: 'text.disabled',
      }
    : undefined;

  return (
    <Tooltip key={label} title={tooltip}>
      <Chip label={label} size="small" color={color} variant="outlined" sx={unattachedStyles} />
    </Tooltip>
  );
};

const renderBundleChip = (bundle: AttachedBundle) => {
  const isLive = isBundleCurrentlyLive(bundle);
  const status = bundle.status === 'draft' ? 'draft' : isLive ? 'live' : 'not currently live';

  return (
    <Tooltip key={bundle.id} title={`Bundle "${bundle.name}" is ${status}`}>
      <Chip
        icon={<LocalOfferIcon />}
        label={bundle.name}
        size="small"
        color={isLive ? 'success' : 'default'}
        variant="outlined"
      />
    </Tooltip>
  );
};

const renderCampaignChip = (campaign: AttachedCampaign) => {
  const isLive = isCampaignCurrentlyLive(campaign);
  const status = campaign.status === 'draft' ? 'draft' : isLive ? 'live' : 'not currently live';

  return (
    <Tooltip key={campaign.id} title={`Campaign "${campaign.displayName}" is ${status}`}>
      <Chip
        icon={<CampaignIcon />}
        label={campaign.displayName}
        size="small"
        color={isLive ? 'success' : 'default'}
        variant="outlined"
      />
    </Tooltip>
  );
};

type SupplierStateChip = {
  flag: IndicativePropertyStateFlag;
  value: boolean;
  label: string;
  tooltip: string;
  color: 'success' | 'warning' | 'info' | 'error';
  outline?: boolean;
  shouldShow: (property: PropertyResponse) => boolean;
};

const SUPPLIER_STATE_CHIPS: Array<SupplierStateChip> = [
  {
    flag: 'hasBedbankRatesEnabled',
    value: false,
    label: 'Bedbank rates disabled',
    tooltip: 'This property only has a Bedbank attached, but Bedbank rates are disabled.',
    color: 'warning',
    shouldShow: (property) =>
      property.hasBedbankMappings &&
      !property.hasReservationMappings &&
      !property.indicativePropertyState.hasFlashOffer &&
      !property.indicativePropertyState.hasBedbankRatesEnabled,
  },
  {
    flag: 'hasBedbankRoomsMapped',
    value: false,
    label: 'Bedbank rooms unmapped',
    tooltip: 'A Bedbank property is attached, but no verified Bedbank room mappings are present.',
    color: 'warning',
    shouldShow: (property) => property.hasBedbankMappings && !property.indicativePropertyState.hasBedbankRoomsMapped,
  },
  {
    flag: 'hasLpcOffer',
    value: false,
    label: 'LPC offer missing',
    tooltip: 'An LPC property is attached, but there is no linked LPC offer.',
    color: 'error',
    shouldShow: (property) =>
      property.hasReservationMappings &&
      !property.indicativePropertyState.hasLpcOffer &&
      !property.indicativePropertyState.hasFlashOffer,
  },
  {
    flag: 'hasApprovedLpcOffer',
    value: false,
    label: 'LPC offer not approved',
    tooltip: 'An LPC offer is linked, but its content is not approved.',
    color: 'warning',
    shouldShow: (property) =>
      property.hasReservationMappings &&
      property.indicativePropertyState.hasLpcOffer &&
      !property.indicativePropertyState.hasApprovedLpcOffer,
  },
  {
    flag: 'hasScheduledLpcOffer',
    value: false,
    label: 'LPC offer unscheduled',
    tooltip: 'An LPC offer is linked, but it does not have an active online purchase schedule.',
    color: 'warning',
    shouldShow: (property) =>
      property.hasReservationMappings &&
      property.indicativePropertyState.hasLpcOffer &&
      !property.indicativePropertyState.hasScheduledLpcOffer,
  },
  {
    flag: 'hasLpcRoomsMapped',
    value: false,
    label: 'LPC rooms unmapped',
    tooltip: 'An LPC property is attached, but no verified LPC room mappings are present.',
    color: 'error',
    shouldShow: (property) => property.hasReservationMappings && !property.indicativePropertyState.hasLpcRoomsMapped,
  },
  {
    flag: 'hasApprovedFlashOffer',
    value: false,
    label: 'Flash offer not approved',
    tooltip: 'A Flash offer is linked, but its content is not approved.',
    color: 'success',
    outline: true,
    shouldShow: (property) =>
      property.indicativePropertyState.hasFlashOffer && !property.indicativePropertyState.hasApprovedFlashOffer,
  },
  {
    flag: 'hasScheduledFlashOffer',
    value: false,
    label: 'Flash offer attached',
    tooltip: 'A Flash offer is linked but not currently scheduled.',
    color: 'success',
    outline: true,
    shouldShow: (property) =>
      property.indicativePropertyState.hasFlashOffer && !property.indicativePropertyState.hasScheduledFlashOffer,
  },
  {
    flag: 'hasScheduledFlashOffer',
    value: true,
    label: 'Active Flash',
    tooltip: 'A Flash offer is linked and currently scheduled, so this property may be off by design.',
    color: 'info',
    shouldShow: (property) =>
      property.indicativePropertyState.hasFlashOffer && property.indicativePropertyState.hasScheduledFlashOffer,
  },
  {
    flag: 'hasUltraLuxSupplier',
    value: true,
    label: 'UltraLux',
    tooltip: 'This property has an UltraLux reservation property attached.',
    color: 'info',
    outline: true,
    shouldShow: (property) => property.indicativePropertyState.hasUltraLuxSupplier,
  },
];

const getSupplierStateChipKey = ({ flag, value }: Pick<SupplierStateChip, 'flag' | 'value'>) => `${flag}:${value}`;

const formatError = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }

  return JSON.stringify(error);
};

/**
 * Displays a paginated grid of accommodation properties with search/filter results.
 * Supports column visibility toggling, server-side pagination, and property deletion.
 */
function AccommodationPropertiesResultsTable({ searchInput, pageIndex, onPageChange }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model);
      onPageChange(0);
    },
    [onPageChange],
  );

  const sortField = sortModel[0]?.field as 'nameScore' | 'distance' | undefined;
  const sortDir = sortModel[0]?.sort;

  const propertiesQueryParams = useMemo(
    () => ({
      name: searchInput.propertyName,
      id: searchInput.id,
      city: searchInput.city,
      country: searchInput.country,
      rating: searchInput.rating,
      categories: searchInput.categories,
      ...getIndicativePropertyStateQueryFilters(searchInput.ratesEnabledFilters),
      ...getIndicativePropertyStateQueryFilters(searchInput.supplierStateFilters),
      ...getIndicativePropertyStateQueryFilters(searchInput.promotionsFilters),
      accommV2Bookable: searchInput.accommV2Bookable ? searchInput.accommV2Bookable === 'true' : undefined,
      page: String((pageIndex ?? 0) + 1),
      limit: String(PAGE_SIZE),
      sortBy: sortField,
      sortOrder: sortDir,
    }),
    [
      pageIndex,
      searchInput.accommV2Bookable,
      searchInput.categories,
      searchInput.city,
      searchInput.country,
      searchInput.id,
      searchInput.propertyName,
      searchInput.promotionsFilters,
      searchInput.ratesEnabledFilters,
      searchInput.rating,
      searchInput.supplierStateFilters,
      sortDir,
      sortField,
    ],
  );

  const propertiesQuery = useGetAccommodationProperties(propertiesQueryParams);

  const handleDeleteProperty = async (propertyId: string) => {
    if (window.confirm('Are you sure you want to delete this property?')) {
      try {
        await deleteProperty(propertyId);
        enqueueSnackbar('Successfully deleted property', { variant: 'success' });
        propertiesQuery.refetch();
      } catch {
        enqueueSnackbar('Failed to delete property', { variant: 'error' });
      }
    }
  };

  const columns: Array<GridColDef<PropertyResponse>> = [
    {
      field: 'name',
      headerName: 'Property Name',
      display: 'flex',
      minWidth: 200,
      flex: 2.5,
      renderCell: (params) => (
        <Link
          href={`/accommodation/properties/${params.row.id}`}
          target="_blank"
          className="accommodation-properties-search-result-link"
        >
          {params.value}
        </Link>
      ),
      hideable: false,
    },
    {
      field: 'chainName',
      headerName: 'Chain Name',
      minWidth: 100,
      flex: 1,
      display: 'flex',
      hideable: true,
      valueGetter: (_value, row) => row.chainName || '-',
    },
    {
      field: 'category',
      headerName: 'Category',
      minWidth: 80,
      flex: 0.8,
      display: 'flex',
      hideable: true,
    },
    {
      field: 'rating',
      headerName: 'Rating',
      width: 70,
      display: 'flex',
      hideable: true,
    },
    {
      field: 'country',
      headerName: 'Country',
      minWidth: 80,
      flex: 1,
      display: 'flex',
      hideable: true,
    },
    {
      field: 'city',
      headerName: 'City',
      minWidth: 80,
      flex: 1,
      display: 'flex',
      hideable: true,
    },
    {
      field: 'supplierRates',
      headerName: 'Rates Enabled',
      minWidth: 200,
      flex: 1.5,
      display: 'flex',
      sortable: false,
      hideable: true,
      renderCell: (params) => {
        const state = params.row.indicativePropertyState;
        if (!state) {
          return null;
        }

        const hasBedbankRatesEnabled = params.row.hasBedbankMappings && state.hasBedbankRatesEnabled;
        const hasLpcRatesEnabled = params.row.hasReservationMappings && state.hasLpcRatesEnabled;
        const hasFlashRatesEnabled = state.hasFlashOffer && state.hasApprovedFlashOffer && state.hasScheduledFlashOffer;
        const hasAnyRatesEnabled = hasBedbankRatesEnabled || hasLpcRatesEnabled || hasFlashRatesEnabled;

        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {renderRateChip('Bedbank', params.row.hasBedbankMappings, state.hasBedbankRatesEnabled, hasAnyRatesEnabled)}
            {renderRateChip('LPC', params.row.hasReservationMappings, state.hasLpcRatesEnabled, hasAnyRatesEnabled)}
            {renderRateChip('Flash', state.hasFlashOffer, hasFlashRatesEnabled, hasAnyRatesEnabled)}
          </Stack>
        );
      },
    },
    {
      field: 'accommV2Bookable',
      headerName: 'Booking enabled',
      width: 160,
      display: 'flex',
      sortable: false,
      hideable: true,
      renderCell: (params) => (
        <AccommodationV2BookableChip accommV2Bookable={params.row.accommV2Bookable} variant="icon" />
      ),
    },
    {
      field: 'promotions',
      headerName: 'Promotions',
      minWidth: 200,
      flex: 1.5,
      display: 'flex',
      sortable: false,
      hideable: true,
      renderCell: (params) => {
        const attachedBundles = params.row.attachedBundles ?? [];
        const attachedCampaigns = params.row.attachedCampaigns ?? [];
        if (attachedBundles.length === 0 && attachedCampaigns.length === 0) {
          return null;
        }

        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {attachedBundles.map(renderBundleChip)}
            {attachedCampaigns.map(renderCampaignChip)}
          </Stack>
        );
      },
    },
    {
      field: 'indicativePropertyState',
      headerName: 'Supplier State',
      minWidth: 420,
      flex: 2.5,
      display: 'flex',
      hideable: true,
      sortable: false,
      renderCell: (params) => {
        const state = params.row.indicativePropertyState;
        if (!state) {
          return null;
        }

        const chips = SUPPLIER_STATE_CHIPS.filter((chip) => chip.shouldShow(params.row));

        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {chips.map((chip) => (
              <Tooltip key={getSupplierStateChipKey(chip)} title={chip.tooltip}>
                <Chip
                  label={chip.label}
                  size="small"
                  color={chip.color}
                  variant={chip.outline ? 'outlined' : 'filled'}
                />
              </Tooltip>
            ))}
          </Stack>
        );
      },
    },
    {
      field: 'nameScore',
      headerName: 'Name Score',
      width: 110,
      display: 'flex',
      hideable: true,
      type: 'number',
      valueFormatter: (value: number | undefined) => (value != null ? value.toFixed(3) : '-'),
    },
    {
      field: 'distance',
      headerName: 'Distance (m)',
      width: 120,
      display: 'flex',
      hideable: true,
      type: 'number',
      valueFormatter: (value: number | undefined) => (value != null ? Math.round(value).toLocaleString() : '-'),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteProperty(params.row.id);
          }}
          color="error"
          size="small"
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      {propertiesQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {formatError(propertiesQuery.error)}
        </Alert>
      )}
      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          // Responsive container ensures horizontal scroll on small screens
          '& .MuiDataGrid-root': {
            minWidth: { xs: 600, sm: 800, md: '100%' },
          },
          '& .MuiDataGrid-cell': {
            py: 1,
          },
        }}
      >
        <DataGrid
          className="accommodation-properties-search-results-table"
          columns={columns}
          rows={propertiesQuery.data?.properties ?? []}
          rowCount={propertiesQuery.data?.total ?? 0}
          rowHeight={ROW_HEIGHT}
          pageSizeOptions={[PAGE_SIZE]}
          paginationMode="server"
          paginationModel={{ page: pageIndex, pageSize: PAGE_SIZE }}
          onPaginationModelChange={({ page }) => onPageChange(page)}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          loading={propertiesQuery.isLoading || propertiesQuery.isFetching}
          autoHeight
          disableColumnFilter
          disableDensitySelector
          initialState={{
            columns: {
              columnVisibilityModel: DEFAULT_HIDDEN_COLUMNS,
            },
          }}
          slots={{
            toolbar: GridToolbar,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
        />
      </Box>
    </>
  );
}

export default AccommodationPropertiesResultsTable;
